import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAuth } from "../AuthenticatedApp";
import { useOrdersContext } from "./OrdersContext";
import { useRequestsContext } from "./RequestsContext";
import {
  buildVenueAlertSnapshot,
  diffVenueAlertSnapshots,
} from "../utils/venueAlertSnapshots";

const STORAGE_KEY = "venueAlertSoundPreference:v1";
const QUIET_WINDOW_MS = 500;
const SOUND_KIND_REQUEST = "request";
const SOUND_KIND_ORDER = "order";

const VenueAlertSoundContext = createContext({
  soundEnabled: true,
  playbackBlocked: false,
  toggleSoundEnabled: () => {},
  primeSound: async () => false,
});

const getInitialSoundEnabled = () => {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "muted";
  } catch (error) {
    return true;
  }
};

const getSoundUrl = (filename) => `${process.env.PUBLIC_URL || ""}/sounds/${filename}`;

export const VenueAlertSoundProvider = ({ children }) => {
  const { userPermissions = [], backendUser = {} } = useAuth() || {};
  const { requests, loading: requestsLoading } = useRequestsContext();
  const {
    pendingOrders,
    preparingOrders,
    completedOrders,
    loading: ordersLoading,
  } = useOrdersContext();

  const [soundEnabled, setSoundEnabled] = useState(getInitialSoundEnabled);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const productionAreaId =
    userPermissions.includes("manage_shift") ? backendUser?.areaId ?? null : null;

  const requestAudioRef = useRef(null);
  const orderAudioRef = useRef(null);
  const previousSnapshotRef = useRef(null);
  const timerRef = useRef(null);
  const isHydratedRef = useRef(false);
  const soundEnabledRef = useRef(soundEnabled);
  const playbackBlockedRef = useRef(playbackBlocked);

  const latestSnapshot = useMemo(
    () =>
      buildVenueAlertSnapshot({
        requests,
        pendingOrders,
        preparingOrders,
        completedOrders,
        areaId: productionAreaId,
      }),
    [completedOrders, pendingOrders, preparingOrders, productionAreaId, requests]
  );

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    playbackBlockedRef.current = playbackBlocked;
  }, [playbackBlocked]);

  useEffect(() => {
    if (typeof Audio === "undefined") {
      return undefined;
    }

    requestAudioRef.current = new Audio(getSoundUrl("venue-request.wav"));
    requestAudioRef.current.preload = "auto";

    orderAudioRef.current = new Audio(getSoundUrl("venue-order.wav"));
    orderAudioRef.current.preload = "auto";

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      [requestAudioRef.current, orderAudioRef.current].forEach((audio) => {
        if (!audio) {
          return;
        }

        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, []);

  const persistPreference = useCallback((enabled) => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, enabled ? "enabled" : "muted");
    } catch (error) {
      // Ignore storage failures and keep runtime state authoritative.
    }
  }, []);

  const getAudioByKind = useCallback((kind) => {
    if (kind === SOUND_KIND_REQUEST) {
      return requestAudioRef.current;
    }

    return orderAudioRef.current;
  }, []);

  const playKind = useCallback(
    async (kind) => {
      if (!soundEnabledRef.current || playbackBlockedRef.current) {
        return false;
      }

      const audio = getAudioByKind(kind);
      if (!audio) {
        return false;
      }

      try {
        audio.pause();
        audio.currentTime = 0;
        await audio.play();
        return true;
      } catch (error) {
        setPlaybackBlocked(true);
        return false;
      }
    },
    [getAudioByKind]
  );

  const primeSound = useCallback(async () => {
    const audio = getAudioByKind(SOUND_KIND_REQUEST);
    if (!audio) {
      return false;
    }

    try {
      audio.pause();
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      setPlaybackBlocked(false);
      return true;
    } catch (error) {
      setPlaybackBlocked(true);
      return false;
    }
  }, [getAudioByKind]);

  const toggleSoundEnabled = useCallback(() => {
    setSoundEnabled((currentValue) => {
      const nextValue = !currentValue;
      persistPreference(nextValue);
      return nextValue;
    });
  }, [persistPreference]);

  useEffect(() => {
    if (ordersLoading || requestsLoading) {
      return undefined;
    }

    if (!isHydratedRef.current) {
      previousSnapshotRef.current = latestSnapshot;
      isHydratedRef.current = true;
      return undefined;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      const previousSnapshot = previousSnapshotRef.current;
      previousSnapshotRef.current = latestSnapshot;

      const { hasRequestAlert, hasOrderAlert } = diffVenueAlertSnapshots(
        previousSnapshot,
        latestSnapshot
      );

      if (hasRequestAlert) {
        await playKind(SOUND_KIND_REQUEST);
        return;
      }

      if (hasOrderAlert) {
        await playKind(SOUND_KIND_ORDER);
      }
    }, QUIET_WINDOW_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [latestSnapshot, ordersLoading, playKind, requestsLoading]);

  const contextValue = useMemo(
    () => ({
      soundEnabled,
      playbackBlocked,
      toggleSoundEnabled,
      primeSound,
    }),
    [playbackBlocked, primeSound, soundEnabled, toggleSoundEnabled]
  );

  return (
    <VenueAlertSoundContext.Provider value={contextValue}>
      {children}
    </VenueAlertSoundContext.Provider>
  );
};

export const useVenueAlertSound = () => useContext(VenueAlertSoundContext);
